/**
 * One identity for a feed item, whatever shape it arrives in.
 *
 * Home stacks three lists that draw from overlapping pools — What's Around asks
 * for `scope=all`, which INCLUDES content from people you follow, so a friend's
 * saved place legitimately qualifies for both it and Friends' Activity. Without
 * a shared key the same card renders twice on one screen, which is what QA
 * found (Stadspark and MAS, 2 of 4 visible cards in each strip).
 *
 * Every Home strip carries raw feed-row-shaped items (`content_type` +
 * `id`/`content_id`, `group_id` for grouped events) — What's On included,
 * since composeTeaser maps its follow pins onto that same shape. One key
 * function covers them all, so the dedupe rule can't drift between strips.
 *
 * Grouped events key on the GROUP, never the instance: a three-night run is one
 * card in What's On and could arrive as any of its instances elsewhere, so
 * matching on event id alone would let a duplicate through.
 */

/**
 * Canonical key for a raw feed row. Returns null when the row carries no usable
 * identity — callers must treat null as "never matches", never as a key, or
 * every unidentifiable item would dedupe against every other one.
 */
export function feedItemKey(item) {
  if (!item) return null;

  // Grouped events collapse to their group across every surface.
  if (item.group_id) return `group:${item.group_id}`;

  const id = item.id ?? item.content_id;
  if (id === undefined || id === null || id === '') return null;

  const type = item.content_type;
  // Events are keyed without their type so an 'event' row and the 'event_group'
  // card feedService promotes it to still collide.
  if (type === 'event' || type === 'event_group') return `event:${id}`;

  // Place-backed content keys on the PLACE, not on the row. The same museum
  // reaches Home two ways: as a `place` card (whose own id IS the Google place
  // id) and as somebody's `activity` (a visit, carrying place_id). Keying on
  // content_type would treat those as different things and render the building
  // twice — which is exactly what QA saw with MAS - Museum aan de Stroom:
  //   What's Around  place    id=ChIJZykwEFr2w0cRmgzGX1tHug4   place_id=null
  //   Friends        activity id=act_1763498371423_59hgipuku   place_id=ChIJZykwEFr2w0cRmgzGX1tHug4
  // An activity with no place_id is not place-backed and keeps its own identity.
  if (type === 'place') return `place:${id}`;
  if (type === 'activity' && item.place_id) return `place:${item.place_id}`;

  return type ? `${type}:${id}` : `id:${id}`;
}

/** Keys for a list of raw feed rows, unioned onto an existing set. */
export function unionFeedKeys(base, items) {
  const out = new Set(base || []);
  for (const item of items || []) {
    const key = feedItemKey(item);
    if (key) out.add(key);
  }
  return out;
}

export default feedItemKey;
